
import React from 'react';
import { Task } from '@/context/TaskContext';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import TaskColumn from './TaskColumn';
import { Skeleton } from '../ui/skeleton';

interface TaskBoardProps {
  tasks: Task[];
  onTaskSelect: (id: string) => void;
  activeTaskId: string | undefined;
  onDragEnd: (result: DropResult) => void;
  loading?: boolean;
  onAddNewTask?: () => void;
  isFullscreen?: boolean;
  isMobile?: boolean;
  isDragInProgress?: boolean;
}

const TaskBoard: React.FC<TaskBoardProps> = ({
  tasks,
  onTaskSelect,
  activeTaskId,
  onDragEnd,
  loading = false,
  onAddNewTask,
  isFullscreen = false,
  isMobile = false,
  isDragInProgress = false
}) => {
  // Filter and sort tasks by status and position
  const todoTasks = tasks
    .filter(task => task.status === 'to-do')
    .sort((a, b) => (a.position || 0) - (b.position || 0));
  
  const inProgressTasks = tasks
    .filter(task => task.status === 'in-progress')
    .sort((a, b) => (a.position || 0) - (b.position || 0));
  
  const doneTasks = tasks
    .filter(task => task.status === 'done')
    .sort((a, b) => (a.position || 0) - (b.position || 0));

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex flex-col gap-3">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-28 w-full" />
          </div>
        ))}
      </div>
    );
  }

  // Always use horizontal kanban board layout for better organization
  // Desktop: To Do → In Progress → Done
  // Mobile: In Progress → To Do → Done
  const desktopOrder = [
    { id: 'to-do', title: 'To Do', tasks: todoTasks, color: 'bg-yellow-400' },
    { id: 'in-progress', title: 'In Progress', tasks: inProgressTasks, color: 'bg-blue-400' },
    { id: 'done', title: 'Done', tasks: doneTasks, color: 'bg-green-400' }
  ];

  const mobileOrder = [
    { id: 'in-progress', title: 'In Progress', tasks: inProgressTasks, color: 'bg-blue-400' },
    { id: 'to-do', title: 'To Do', tasks: todoTasks, color: 'bg-yellow-400' },
    { id: 'done', title: 'Done', tasks: doneTasks, color: 'bg-green-400' }
  ];

  const columnOrder = isMobile ? mobileOrder : desktopOrder;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className={`w-full max-w-[1200px] mx-auto relative overflow-visible ${isDragInProgress ? 'dragging-active' : ''}`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-6 overflow-visible">
          {columnOrder.map((column) => (
            <div key={column.id} className="flex flex-col min-h-[400px] overflow-visible">
              <div className="py-2">
                <div className="flex items-center mb-4">
                  <div className={`w-3 h-3 ${column.color} rounded-full mr-2`}></div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{column.title}</h3>
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                    ({column.tasks.length})
                  </span>
                </div>
              </div>
              <div className="flex-1 overflow-visible">
                <TaskColumn
                  title={column.title}
                  droppableId={column.id}
                  tasks={column.tasks}
                  colorClass={column.color}
                  onTaskSelect={onTaskSelect}
                  activeTaskId={activeTaskId}
                  onAddNewTask={onAddNewTask}
                  emptyStateText={`Add a ${column.title.toLowerCase()} task`}
                  hideHeader={true}
                  isDragInProgress={isDragInProgress}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </DragDropContext>
  );
};

export default TaskBoard;
